import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router, navigateTo } from '../../services/router.js';
import { ChatStateController } from '../../controllers/chat-state-controller.js';
import * as chatApi from '../../services/chat-api.js';
import * as agentApi from '../../services/agent-api.js';
import type { AgentSummary } from '../../types/agent.js';
import '../chat/cp-agent-chat-panel.js';
import '../chat/cp-chat-sidebar.js';
import '../chat/cp-chat-voice-overlay.js';
import '../agents/cp-agent-selector.js';

@customElement('cp-page-chat')
export class CpPageChat extends LitElement {
  createRenderRoot() { return this; }

  @state() private _initialized = false;
  @state() private _isVoiceActive = false;
  @state() private _agents: AgentSummary[] = [];
  @state() private _selectedAgentId: number | null = null;

  private _chatState = new ChatStateController(this);

  connectedCallback(): void {
    super.connectedCallback();
    void this._init();
  }

  private async _init(): Promise<void> {
    const params = router.getParams();
    const conversationIdParam = params['conversationId'];

    await Promise.all([
      this._chatState.loadConversations(),
      this._loadAgents(),
    ]);

    if (conversationIdParam) {
      await this._chatState.loadConversation(Number(conversationIdParam));
      this._selectedAgentId = this._chatState.activeAgent?.id ?? this._getMainAgentId();
    } else {
      const mainAgentId = this._getMainAgentId();
      this._selectedAgentId = mainAgentId;
      const newId = await this._chatState.createConversation(mainAgentId ?? undefined);
      navigateTo(`/chat/${newId}`);
    }

    this._initialized = true;
    this.requestUpdate();
  }

  private async _loadAgents(): Promise<void> {
    try {
      this._agents = await agentApi.getAgents();
    } catch (err) {
      console.warn('Failed to load agents:', err);
      this._agents = [];
    }
  }

  private _getMainAgentId(): number | null {
    const main = this._agents.find(a => a.is_main === 1);
    return main?.id ?? null;
  }

  private async _onAgentSelected(e: CustomEvent<{ agentId: number }>): Promise<void> {
    const agentId = e.detail.agentId;
    this._selectedAgentId = agentId;

    // If current conversation has no messages yet, recreate with new agent
    if (this._chatState.messages.length === 0) {
      const newId = await this._chatState.createConversation(agentId);
      navigateTo(`/chat/${newId}`);
    }
  }

  private async _onMessageSend(e: CustomEvent<{ content: string; attachmentIds?: number[] }>): Promise<void> {
    await this._chatState.sendMessage(e.detail.content, e.detail.attachmentIds);
  }

  private _onAttachmentAdded(e: CustomEvent): void {
    this._chatState.addPendingAttachment(e.detail);
  }

  private _onAttachmentRemoved(e: CustomEvent<{ id: number }>): void {
    this._chatState.removePendingAttachment(e.detail.id);
  }

  private async _onConversationSelect(e: CustomEvent<{ id: number }>): Promise<void> {
    const id = e.detail.id;
    await this._chatState.loadConversation(id);
    this._selectedAgentId = this._chatState.activeAgent?.id ?? this._getMainAgentId();
    navigateTo(`/chat/${id}`);
  }

  private async _onConversationNew(): Promise<void> {
    const agentId = this._selectedAgentId ?? this._getMainAgentId();
    const newId = await this._chatState.createConversation(agentId ?? undefined);
    navigateTo(`/chat/${newId}`);
  }

  private async _onTitleRename(e: CustomEvent<{ id: number; title: string }>): Promise<void> {
    const { id, title } = e.detail;
    await chatApi.updateTitle(id, title);
  }

  private async _onConversationDelete(e: CustomEvent<{ id: number; title: string }>): Promise<void> {
    const { id, title } = e.detail;
    if (!window.confirm(`Konversation "${title}" wirklich loeschen?`)) return;
    await chatApi.deleteConversation(id);
    await this._chatState.loadConversations();

    if (id === this._chatState.conversationId) {
      const agentId = this._selectedAgentId ?? this._getMainAgentId();
      const newId = await this._chatState.createConversation(agentId ?? undefined);
      navigateTo(`/chat/${newId}`);
    }
  }

  private _onVoiceStart(): void {
    if (!this._chatState.conversationId) return;
    this._isVoiceActive = true;
  }

  private async _onVoiceCallEnded(): Promise<void> {
    this._isVoiceActive = false;
    if (this._chatState.conversationId) {
      await this._chatState.loadConversation(this._chatState.conversationId);
    }
  }

  private _renderLoading() {
    return html`
      <div class="cp-page-chat__loading">
        <span>Lade Chat...</span>
      </div>
    `;
  }

  private _renderError() {
    return html`
      <div class="cp-page-chat__error">
        <span>${this._chatState.error ?? 'Fehler beim Laden'}</span>
      </div>
    `;
  }

  private _renderAgentSelector() {
    if (this._agents.length <= 1) return nothing;

    const hasMessages = this._chatState.messages.length > 0;

    return html`
      <cp-agent-selector
        .value=${this._selectedAgentId}
        ?disabled=${hasMessages || this._chatState.isStreaming}
        @agent-selected=${this._onAgentSelected}
      ></cp-agent-selector>
    `;
  }

  render() {
    if (this._chatState.loadingState === 'error') {
      return this._renderError();
    }

    if (!this._initialized) {
      return this._renderLoading();
    }

    const messages = this._chatState.messages;
    const isStreaming = this._chatState.isStreaming;
    const conversations = this._chatState.conversations;
    const activeConversationId = this._chatState.conversationId;
    const activeAgent = this._chatState.activeAgent;

    return html`
      <div class="cp-page-chat">
        <!-- Conversation Sidebar -->
        <aside class="cp-page-chat__sidebar">
          <cp-chat-sidebar
            .conversations=${conversations}
            .activeConversationId=${activeConversationId}
            @conversation-select=${this._onConversationSelect}
            @conversation-new=${this._onConversationNew}
            @title-rename=${this._onTitleRename}
            @conversation-delete=${this._onConversationDelete}
          ></cp-chat-sidebar>
        </aside>

        <!-- Chat Area -->
        <main class="cp-page-chat__content">
          ${this._chatState.loadingState === 'loading' && !this._initialized
            ? this._renderLoading()
            : html`
              <!-- Toolbar (visible only when no voice call active) -->
              ${!this._isVoiceActive ? html`
                <div class="cp-page-chat__toolbar">
                  ${this._renderAgentSelector()}
                  <button
                    class="cp-page-chat__model-btn ${this._chatState.model === 'opus' ? 'cp-page-chat__model-btn--active' : ''}"
                    @click=${() => this._chatState.toggleModel()}
                    ?disabled=${isStreaming}
                    title="Model wechseln: ${this._chatState.model === 'sonnet' ? 'Sonnet (schnell)' : 'Opus (ausfuehrlich)'}"
                  >
                    ${this._chatState.model === 'opus' ? 'Opus' : 'Sonnet'}
                  </button>
                  <button
                    class="cp-page-chat__voice-btn"
                    @click=${() => this._onVoiceStart()}
                    ?disabled=${!activeConversationId || isStreaming}
                    title="Voice-Modus starten"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/>
                      <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                    Voice
                  </button>
                </div>
              ` : nothing}

              <cp-agent-chat-panel
                .messages=${messages}
                ?isStreaming=${isStreaming}
                .activeAgent=${activeAgent}
                .conversationId=${activeConversationId}
                .pendingAttachments=${this._chatState.pendingAttachments}
                @message-send=${this._onMessageSend}
                @attachment-added=${this._onAttachmentAdded}
                @attachment-removed=${this._onAttachmentRemoved}
              ></cp-agent-chat-panel>

              <!-- Voice Overlay -->
              ${this._isVoiceActive && activeConversationId ? html`
                <cp-chat-voice-overlay
                  .conversationId=${activeConversationId}
                  @voice-call-ended=${() => this._onVoiceCallEnded()}
                ></cp-chat-voice-overlay>
              ` : nothing}
            `
          }
        </main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-chat': CpPageChat;
  }
}
